export type PayrollStatus =
  | 'OPEN' | 'CALCULATED' | 'LOCKED' | 'APPROVED' | 'PAID' | 'CLOSED'

export type ElementType = 'EARNING' | 'DEDUCTION' | 'EMPLOYER_CONTRIBUTION' | 'INFORMATIONAL'

export type CalculationType =
  | 'FIXED' | 'PERCENTAGE' | 'FORMULA' | 'MANUAL' | 'DAILY_RATE' | 'HOURLY_RATE'

export type ProvisionType = 'GRATUITY' | 'LEAVE_SALARY' | 'AIR_TICKET' | 'OTHER'

/**
 * What each state means for what can still change.
 *
 * The wording matters more than the name: "locked" tells somebody nothing, whereas "inputs
 * frozen so the figures under review cannot move" tells them why they cannot edit a
 * timesheet for that month.
 */
export const PAYROLL_STATUS_LABELS: Record<PayrollStatus, string> = {
  OPEN: 'Open',
  CALCULATED: 'Calculated',
  LOCKED: 'Locked for review',
  APPROVED: 'Approved',
  PAID: 'Paid',
  CLOSED: 'Closed',
}

export const PAYROLL_STATUS_NOTES: Record<PayrollStatus, string> = {
  OPEN: 'Inputs may still change. Recalculating is free.',
  CALCULATED: 'Figures exist and can be recalculated as often as you like.',
  LOCKED: 'Inputs are frozen so the figures under review cannot move beneath the reviewer.',
  APPROVED: 'The journal has been posted. Payment has not been released.',
  PAID: 'Money has left. From here a correction is a reversal, not an edit.',
  CLOSED: 'The period is finished. Leave and attendance inside it are blocked.',
}

export const ELEMENT_TYPE_LABELS: Record<ElementType, string> = {
  EARNING: 'Earning',
  DEDUCTION: 'Deduction',
  EMPLOYER_CONTRIBUTION: "Employer's cost",
  INFORMATIONAL: 'Informational — never reaches pay',
}

export const CALCULATION_TYPE_LABELS: Record<CalculationType, string> = {
  FIXED: 'A fixed amount',
  PERCENTAGE: 'A percentage of another element',
  FORMULA: 'A formula',
  MANUAL: 'Entered against each employee',
  DAILY_RATE: 'A daily rate',
  HOURLY_RATE: 'An hourly rate',
}

export const PROVISION_TYPE_LABELS: Record<ProvisionType, string> = {
  GRATUITY: 'End-of-service gratuity',
  LEAVE_SALARY: 'Leave salary',
  AIR_TICKET: 'Air ticket',
  OTHER: 'Other',
}

export interface PayrollRunView {
  id: string
  legalEntityId: string | null
  establishmentId: string | null
  periodYear: number
  periodMonth: number
  periodStart: string
  periodEnd: string
  payDate: string | null
  status: PayrollStatus
  employeeCount: number
  totalGross: number
  totalDeductions: number
  totalNet: number
  totalEmployerCost: number
  currency: string | null
  reopenCount: number
  calculatedAt: string | null
  lockedAt: string | null
  approvedAt: string | null
  paidAt: string | null
  /** Only these moves exist; the screen offers no button for anything else. */
  allowedNext: PayrollStatus[]
}

export interface PayrollRunEventView {
  id: string
  fromStatus: string | null
  toStatus: string
  reason: string | null
  actorUserId: string | null
  actorEmail: string | null
  occurredAt: string
}

export interface PayrollLineView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  elementId: string
  elementCode: string | null
  elementName: string | null
  elementType: string | null
  sliceFrom: string | null
  sliceTo: string | null
  prorateFactor: number
  baseAmount: number | null
  amount: number
  currency: string | null
  glAccountCode: string | null
  costCenterId: string | null
  /** Null on lines calculated before the run recorded its working. */
  calculationTraceId: string | null
}

/** A period this person was paid in, as the self-service list shows it. */
export interface MyPayslipView {
  runId: string
  periodYear: number
  periodMonth: number
  periodStart: string
  periodEnd: string
  currency: string | null
  grossPay: number
  totalDeductions: number
  netPay: number
  status: string
  /** Set while a run is approved but not yet paid, so "approved" is not read as "in the bank". */
  paymentNote: string | null
}

export interface PayslipView {
  runId: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  periodStart: string
  periodEnd: string
  currency: string | null
  earnings: PayrollLineView[]
  deductions: PayrollLineView[]
  employerContributions: PayrollLineView[]
  informational: PayrollLineView[]
  grossPay: number
  totalDeductions: number
  netPay: number
  employerCost: number
}

export interface ExplainView {
  lineId: string
  elementCode: string | null
  amount: number | null
  currency: string | null
  calculationType: string | null
  rule: string | null
  ruleVersionId: string | null
  inputs: Record<string, unknown>
  working: Record<string, unknown>
  approverChain: unknown[]
  calculatedAt: string | null
  calculatedBy: string | null
  /** Set when the line has no trace; explains why rather than showing an empty panel. */
  unavailableReason: string | null
}

export interface PayElementView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  elementType: string
  category: string | null
  evaluationOrder: number
  prorateOnJoinExit: boolean
  prorateOnUnpaidLeave: boolean
  arrearsCapable: boolean
  taxable: boolean
  statutory: boolean
  wpsIncluded: boolean
  affectsGratuity: boolean
  affectsGpssa: boolean
  currency: string | null
  glAccountCode: string | null
  active: boolean
  /** Why a person's own amount here would not be paid, or null when it would. */
  amountProblem: string | null
}

export interface PayElementVersionView {
  id: string
  elementId: string
  versionNo: number
  effectiveFrom: string
  effectiveTo: string | null
  calculationType: string
  fixedAmount: number | null
  percentage: number | null
  percentageOfCode: string | null
  formula: string | null
  minimumAmount: number | null
  maximumAmount: number | null
  notes: string | null
}

export interface EmployeePayElementView {
  id: string
  employeeId: string
  elementId: string
  elementCode: string | null
  elementName: string | null
  /** EARNING, DEDUCTION… — null only when the element itself could not be found. */
  elementType: string | null
  effectiveFrom: string
  effectiveTo: string | null
  amount: number | null
  percentage: number | null
  currency: string | null
  notes: string | null
}

export interface JournalLineView {
  id: string
  accountCode: string | null
  costCenterId: string | null
  employeeId: string | null
  elementId: string | null
  debit: number
  credit: number
  memo: string | null
  /** True when the element carries no GL code — reported, never defaulted. */
  unmapped: boolean
}

export interface JournalBatchView {
  id: string
  batchType: string
  periodYear: number
  periodMonth: number
  reversesBatchId: string | null
  status: string
  totalDebit: number
  totalCredit: number
  currency: string | null
  exportedAt: string | null
  notes: string | null
  lines: JournalLineView[]
  unmappedLineCount: number
}

export interface ProvisionLedgerView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  provisionType: string
  periodYear: number
  periodMonth: number
  openingBalance: number
  accrual: number
  utilisation: number
  adjustment: number
  closingBalance: number
  currency: string | null
  basisAmount: number | null
  basisNote: string | null
  /** The balance carried in from the previous system's books, when there was one. */
  broughtForward: number | null
  /** The month end it was true on. */
  broughtForwardAsOf: string | null
  /** What HUMIS has accrued on top since, through this month. */
  accruedSince: number | null
}

export interface CalculationResultView {
  employees: number
  gross: number
  deductions: number
  employerCost: number
  lines: number
}

export interface ProvisionResultView {
  employees: number
  accrued: number
  closing: number
}

/** Which mechanism defines the basic wage gratuity accrues on. */
export type BasicPayBasisSource = 'ELEMENT_FLAG' | 'ELEMENT_CODE' | 'NOT_CONFIGURED'

export interface BasicPayBasisView {
  source: BasicPayBasisSource
  elementCodes: string[]
  note: string
}

export interface GpssaConfigView {
  id: string
  legalEntityId: string | null
  nationality: string
  effectiveFrom: string
  effectiveTo: string | null
  employeeRate: number
  employerRate: number
  governmentRate: number
  wageBaseRule: string
  minimumWage: number | null
  maximumWage: number | null
}

export interface GpssaContributionView {
  id: string
  employeeId: string
  employeeName: string | null
  nationality: string
  periodYear: number
  periodMonth: number
  contributoryWage: number
  employeeAmount: number
  employerAmount: number
  currency: string | null
}

export interface AirTicketEntitlementView {
  id: string
  legalEntityId: string | null
  gradeId: string | null
  contractType: string | null
  effectiveFrom: string
  effectiveTo: string | null
  frequencyMonths: number
  cabinClass: string
  adultCount: number
  childCount: number
  estimatedCost: number
  currency: string | null
  encashable: boolean
  monthlyAccrual: number
}

export interface AirTicketClaimView {
  id: string
  employeeId: string
  employeeName: string | null
  entitlementId: string | null
  leaveRequestId: string | null
  claimType: string
  claimedOn: string
  entitlementFrom: string | null
  entitlementTo: string | null
  amount: number
  currency: string | null
  status: string
}

/** One person's latest closing balance for a provision type. */
export interface ProvisionBalanceView {
  provisionType: ProvisionType
  periodYear: number
  periodMonth: number
  closingBalance: number
  /** What the latest month added. */
  lastAccrual: number
  currency: string | null
  basisAmount: number | null
  basisNote: string | null
}

/**
 * What payroll owes one person and has not paid, for the profile.
 *
 * A provision type with no month on file is absent from `balances` rather than zero —
 * "never accrued" and "owed nothing" are different answers.
 */
export interface EmployeeProvisionsView {
  balances: ProvisionBalanceView[]
  airTicketEntitlement: AirTicketEntitlementView | null
  airTicketClaims: AirTicketClaimView[]
}

export function monthName(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleString('en', { month: 'long', year: 'numeric' })
}
